import React, { useContext, useState, useMemo, useCallback, useRef } from 'react';
import classnames from 'classnames';
import { TabContent, Option } from './types';
import { WalletSortAndFilterContext } from './context';
import { OutlinedButton } from 'components/outlined-btn/OutlinedButton';
import Slider from 'react-slick';
import useMediaQueries from 'hooks/useMediaQueries';
import { CarouselControl } from 'components/carousel/CarouselControl';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

/* Computes the appropriate amount of slides based on the number of brands passed.
 * Mainly used to show the slides in a nice manner and balancing the amount
 * shown per slide.
 *
 * Returns an array of length 2 where,
 *  - the first index is the amount  the carousel should render.
 *  - the second index is the amount of SLIDES PER ROW
 *
 * These two values map to the rows/slidesPerRow config for the Slider
 */
const getSlidesConfig = (brands?: Option[]) => {
    const brands_ = brands as Option[];
    const numberOfBrands = brands_.length;

    switch (numberOfBrands) {
        case 1:
        case 2:
        case 3:
            return [1, 3];
        case 4:
            return [2, 2];
        default:
            return [2, 3];
    }
};

export const BrandTab = ({ brands, isActive }: TabContent) => {
    const [cardWidth, setCardWidth] = useState(224); // biased towards desktop
    const containerRef = useRef<HTMLDivElement>(null);
    const [rows, slidesPerRow] = useMemo(() => getSlidesConfig(brands), [brands]);
    const [slider, setSlider] = useState<Slider>();
    const { selectedBrands, setSelectedBrands } = useContext(WalletSortAndFilterContext);

    // computes the width of the container of the carousel
    // REQUIRED for the carousel to render properly, otherwise wild widths of slides get
    // generated by the Slider component
    const padding = 5;
    const width = (cardWidth + padding) * slidesPerRow;

    const setSliderNode = useCallback((node: Slider) => {
        if (node !== null) {
            setSlider(node);
        }
    }, []);

    // Adds/Removes selected brand from the selections array
    const onClick = (option: Option) => {
        setSelectedBrands((selections: Option[]) => {
            const isAlreadySelected = selections.some(
                (selection: Option) => option.value === selection.value
            );
            return isAlreadySelected
                ? selections.filter((selection: Option) => option.value !== selection.value)
                : [...selections, option];
        });
    };

    const queries = useMemo(
        () => ({
            '(max-width: 767px)': () => {
                setCardWidth(144);
            },
            '(min-width: 768px)': () => {
                setCardWidth(224);
            },
        }),
        []
    );

    useMediaQueries(queries);

    const settings = useMemo(() => {
        return {
            slidesToShow: 1,
            arrows: false,
            // don't allow dragging on desktop: Bug where click event during the start drag event
            // flows to the card which redirects to the cards details page after the drag event ends
            // Note: There's an inconsistent way to get around this via callbacks to the Slider but
            // does not work very consistently
            draggable: false,
            rows,
            slidesPerRow,
            dots: true,
            appendDots: (dots: React.ReactNode) => (
                <CarouselControl
                    prev={slider?.slickPrev}
                    next={slider?.slickNext}
                    dots={dots}
                    showDots={true}
                />
            ),
        };
    }, [rows, slidesPerRow, slider]);

    return (
        <div
            className={classnames('mt-7 mx-auto w-screen', isActive ? '' : 'hidden')}
            ref={containerRef}
            style={{ width }}
        >
            <Slider ref={setSliderNode} {...settings}>
                {brands?.map((brand: Option) => (
                    <div key={brand.value} className="mb-3">
                        <OutlinedButton
                            className="block m-auto flex items-center"
                            active={selectedBrands.some(
                                (option: Option) => option.value === brand.value
                            )}
                            onClick={() => onClick(brand)}
                        >
                            {brand.imageUrl && (
                                <span className="px-2">
                                    <img width="40" src={brand.imageUrl} />
                                </span>
                            )}
                            <span>{brand.text}</span>
                        </OutlinedButton>
                    </div>
                ))}
            </Slider>
        </div>
    );
};
